import assign from 'lodash/assign';
import map from 'lodash/map';

import buildingUtils from 'app/shared/utils/buildingUtils';
import dateUtils from 'app/shared/utils/dateUtils';

import Geo from 'app/shared/models/Geo';
import Details from 'app/shared/models/Details';
import Address from 'app/shared/models/Address';
import RecencyTime from 'app/shared/models/RecencyTime';
import School from 'app/shared/models/School';
import { logError } from '@zg-rentals/log-error';
import type Photo from 'app/shared/models/Photo';
import type ListingAreas from 'app/shared/models/ListingAreas';
import type { ListingAreasApiResponse } from 'app/types/areas.type';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { UnitV2Type } from 'app/shared/models/UnitV2';

// Pick only the properties that BuildingV2 actually uses from UnitV2
type Unit = Pick<
  UnitV2Type,
  | 'address'
  | 'aliasEncoded'
  | 'areas'
  | 'featuredPropertyProductType'
  | 'isApartmentBldg'
  | 'propertyType'
  | 'searchKeyword'
  | 'previewPhoto'
  | 'uriBuilding'
  | 'uriMalone'
>;

type BuildingV2Input = {
  areas?: ListingAreasApiResponse;
  availableUnits?: Record<string, unknown>;
  geo?: Record<string, unknown>;
  lotIdEncoded?: string;
  listings?: Array<ListingDetails>;
  lotIdReviews?: Record<string, unknown>;
  schools?: Array<Record<string, unknown>>;
};

type BuildingV2Instance = {
  active: boolean;
  address: Address;
  aliasEncoded: string;
  areas: ReturnType<typeof ListingAreas>;
  building: boolean;
  isBuilding: boolean;
  displayName: string;
  details: ReturnType<typeof Details>;
  featuredPropertyProductType: string;
  geo: Geo;
  iconType: string;
  isApartmentBldg: boolean;
  listingMinMaxPriceBeds: unknown;
  maloneLotIdEncoded: string;
  propertyType: string;
  recencyTime: RecencyTime;
  searchKeyword: string;
  previewPhoto?: Photo;
  photos: Array<unknown>;
  largePhotos: Array<unknown>;
  thumbPhotos: Array<unknown>;
  uriV2: string;
  userItemTypes: Array<unknown>;
  units: Array<Unit>;
  rentalUnits: Array<unknown>;
  schools: Array<School>;
  pricingSummary: unknown;
};

type BuildingV2Constructor = (input?: BuildingV2Input) => BuildingV2Instance | false;

const BuildingV2: BuildingV2Constructor = function (
  this: BuildingV2Instance,
  {
    areas = {},
    availableUnits = {},
    geo = {},
    lotIdEncoded = '',
    listings = [{}] as Array<ListingDetails>,
    lotIdReviews = {},
    schools = [],
  } = {},
) {
  try {
    const {
      units,
      rentalUnits,
      pricingSummary,
      isActive,
      buildingPhotos,
      buildingLargePhotos,
      buildingThumbPhotos,
      buildingActivated,
      buildingUserItemTypes,
      listingMinMaxPriceBeds,
    } = buildingUtils.processApiDataForBuildingV2({
      areas,
      availableUnits,
      geo,
      listings,
      lotIdReviews,
      schools,
    }) as unknown as {
      units: Array<Unit>;
      rentalUnits: Array<unknown>;
      pricingSummary: unknown;
      isActive: boolean;
      buildingPhotos: Array<unknown>;
      buildingLargePhotos: Array<unknown>;
      buildingThumbPhotos: Array<unknown>;
      buildingActivated: Date | string;
      buildingUserItemTypes: Array<unknown>;
      listingMinMaxPriceBeds: unknown;
    };
    const hasMultipleUnits = units.length > 1;
    const neighborhood = (areas.specific && areas.specific.name) || '';
    this.active = isActive;
    this.address = new Address(assign(units[0].address, { neighborhood }));
    this.aliasEncoded = units[0].aliasEncoded;
    this.areas = units[0].areas;
    this.building = true;
    this.isBuilding = true;
    this.displayName = buildingUtils.getBuildingDisplayName(units) || '';
    // @ts-expect-error - Details is a constructor function with @ts-nocheck, not a proper class
    this.details = new Details({
      title: buildingUtils.getBuildingPageTitle(units[0].address), // page title
    });
    this.featuredPropertyProductType = String(units[0].featuredPropertyProductType || '');
    this.geo = new Geo(geo);
    this.iconType = 'condo';
    this.isApartmentBldg = Boolean(units[0].isApartmentBldg);
    this.listingMinMaxPriceBeds = listingMinMaxPriceBeds;
    this.maloneLotIdEncoded = String(lotIdEncoded);
    this.propertyType = String(units[0].propertyType || '');
    this.recencyTime = new RecencyTime(dateUtils.recencyTime(buildingActivated, new Date()));
    this.searchKeyword = hasMultipleUnits
      ? String(units.length + ' Units Available')
      : String(units[0].searchKeyword || '');
    this.previewPhoto = units[0].previewPhoto;
    this.photos = buildingPhotos;
    this.largePhotos = buildingLargePhotos;
    this.thumbPhotos = buildingThumbPhotos;
    this.uriV2 = hasMultipleUnits ? String(units[0].uriBuilding) : String(units[0].uriMalone);
    this.userItemTypes = buildingUserItemTypes;
    this.units = units;
    this.rentalUnits = rentalUnits;
    this.schools = map(schools, (school) => {
      return new School(school);
    });
    this.pricingSummary = pricingSummary;
  } catch (err) {
    logError({
      error: err as Error,
      errorType: 'DataModelError',
      context: 'BuildingV2',
    });
    return false;
  }
} as BuildingV2Constructor;

export default BuildingV2;
